import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { isEqual } from 'lodash';
import { isFlexibleStory } from 'lib/FlexibleLayoutUtils';
import { ViewBlockSection } from '../components/ViewBlockSection';
import { FeatureFlags } from '@socrata/platform/feature-flags';
import { ClassicBlockSection } from 'editor/renderers/ClassicBlockSection';
import { selectors as storySelectors } from 'store/selectors/StorySelectors';
import { getHasReportBeenRun } from 'store/selectors/DataSourceSelectors';
import { BlankStoryIllustration } from '../components/BlankStoryIllustration';
import { ViewStoryBar } from '../components/ViewStoryBar';
import { inPrintMode } from 'util/in-print-mode';
import { EnvironmentFactory } from 'StorytellerEnvironment';
import { type BlockWithBlockId } from 'store/selectors/StorySelectors/Blocks';
import { ComponentType } from 'types';
import { FullPageLoading } from 'lib/components/full-page-loading/full-page-loading';

export function ViewStory({ onFinishRenderCallback }: { onFinishRenderCallback?: () => void }) {
  const Environment = EnvironmentFactory();

  useEffect(() => {
    onFinishRenderCallback?.();
  }, [onFinishRenderCallback]);

  const useApplyAllFilters = !inPrintMode() && FeatureFlags.valueOrDefault('enable_batch_running_filters');
  const hasReportBeenRun = useSelector(getHasReportBeenRun);

  // When using apply all filters and there are pending changes, only show global filter blocks + illustration
  // But only show the blank illustration if the report has never been run before
  const shouldShowBlankStory = useMemo(
    () => useApplyAllFilters && !hasReportBeenRun,
    [hasReportBeenRun, useApplyAllFilters],
  );

  const blocks = useSelector(storySelectors.getStoryBlocks(Environment.STORY_UID!), isEqual);

  return (
    <>
      <div>
        {FeatureFlags.valueOrDefault('enable_derived_reports') && <ViewStoryBar />}
        {!shouldShowBlankStory &&
          blocks.map((block, index) => (
            <Block key={block.blockId} index={index} block={block} totalBlocks={blocks.length} />
          ))}
        {shouldShowBlankStory && <GfbOnly />}
      </div>
      <FullPageLoading />
    </>
  );
}

/**
 * Wrapper component to figure out whether we should do flexible story or classic.
 */
function Block({
  block,
  index = 0,
  totalBlocks,
}: {
  block: BlockWithBlockId;
  index?: number;
  totalBlocks: number;
}) {
  const Environment = EnvironmentFactory();

  if (isFlexibleStory()) {
    return <ViewBlockSection components={block.components} blockId={block.blockId} />;
  } else {
    return (
      <ClassicBlockSection
        blockId={block.blockId}
        components={block.components}
        storyUid={Environment.STORY_UID!}
        blockIndex={index}
        blockCount={totalBlocks}
        editMode={false}
      />
    );
  }
}

/**
 * Find the GFB and display that only, alongside the blank.
 */
function GfbOnly() {
  const Environment = EnvironmentFactory();
  const blocks = useSelector(storySelectors.getStoryBlocks(Environment.STORY_UID!), isEqual);
  const gfb = useMemo(() => {
    return blocks.find(({ components }) => components[0]?.type === ComponentType.GLOBAL_FILTER) ?? null;
  }, [blocks]);

  return (
    <>
      {gfb && <Block block={gfb} totalBlocks={1} />}
      <BlankStoryIllustration />
    </>
  );
}
